import { cleanTextForTts } from "../../lib/tts/cleanTextForTts.js";

const TTS_URL = import.meta.env.VITE_TTS_SPEAK_URL || "/api/tts/speak";

let currentAudio = null;
/** Bumped on every cancel / new speak — in-flight fetches must not play after cancel. */
let speakGeneration = 0;
/** @type {AbortController | null} */
let currentAbort = null;

function normalizeClientLang(lang) {
  const raw = String(lang || "en-US").trim().toLowerCase().replace("_", "-");
  if (raw.startsWith("ru")) return "ru-RU";
  return "en-US";
}

function speakWithBrowser(text, lang, onEnd, onError, generation) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    if (generation === speakGeneration) onEnd?.();
    return;
  }
  if (generation !== speakGeneration) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.rate = 0.9;
  utterance.onend = () => {
    if (generation === speakGeneration) onEnd?.();
  };
  utterance.onerror = () => {
    if (generation !== speakGeneration) return;
    onError?.();
    onEnd?.();
  };
  window.speechSynthesis.speak(utterance);
}

export function cancelSpeech() {
  speakGeneration += 1;
  if (currentAbort) {
    try {
      currentAbort.abort();
    } catch {
      /* ignore */
    }
    currentAbort = null;
  }
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Speak via Piper API (English + Russian); fallback to browser on errors.
 * @param {string} text
 * @param {{ lang?: string, voice?: string, onEnd?: () => void, onError?: () => void }} options
 *   voice — Russian only: "irina" (default) or "dmitri"
 */
export async function speakEnglish(text, options = {}) {
  const { voice, onEnd, onError } = options;
  const lang = normalizeClientLang(options.lang);
  const cleaned = cleanTextForTts(text, lang);
  if (!cleaned) {
    onEnd?.();
    return;
  }

  cancelSpeech();
  const myGen = speakGeneration;
  const ac = new AbortController();
  currentAbort = ac;

  try {
    const payload = { text: cleaned, lang };
    // Always pin Russian to a Piper voice (Irina by default — same as Export Audio).
    // Omitting voice can fall through to a poor browser synthesis path on some setups.
    if (lang === "ru-RU") {
      payload.voice = voice || "irina";
    }

    const res = await fetch(TTS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: ac.signal,
    });

    if (myGen !== speakGeneration) return;

    if (!res.ok) {
      throw new Error(`TTS HTTP ${res.status}`);
    }

    const blob = await res.blob();
    if (myGen !== speakGeneration) return;

    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudio = audio;

    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
      if (myGen === speakGeneration) onEnd?.();
    };

    audio.onerror = () => {
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
      if (myGen !== speakGeneration) return;
      onError?.();
      speakWithBrowser(cleaned, lang, onEnd, onError, myGen);
    };

    await audio.play();
    if (myGen !== speakGeneration) {
      audio.pause();
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
    }
  } catch (err) {
    if (err?.name === "AbortError" || myGen !== speakGeneration) return;
    onError?.();
    speakWithBrowser(cleaned, lang, onEnd, onError, myGen);
  } finally {
    if (currentAbort === ac) currentAbort = null;
  }
}

/**
 * Global TTS helpers + delegated click for `.ex-tts-btn` / `[data-ex-marker=tts]`.
 * Prefer `data-tts` over inline onclick (apostrophes break HTML attribute quoting).
 */
export function registerGlobalSpeak() {
  if (typeof window === "undefined") return;
  window.speakText = (text) => {
    speakEnglish(text, { lang: "en-US" });
  };
  window.speakTextRu = (text) => {
    speakEnglish(text, { lang: "ru-RU", voice: "irina" });
  };

  if (window.__exTtsClickBound) return;
  window.__exTtsClickBound = true;
  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target?.closest?.(".ex-tts-btn, [data-ex-marker='tts']");
      if (!btn) return;
      const spoken = btn.getAttribute("data-tts");
      if (spoken == null) return;
      e.preventDefault();
      e.stopPropagation();
      window.speakText(spoken);
    },
    true
  );
}
